/**
 * §11 `CaseMapper` — locale-aware case mapping, injected.
 *
 * `String.prototype.toLocaleUpperCase` is banned by contracts/adapter.md §9: it is ICU-backed,
 * untestable, unswappable, has no mechanical Python equivalent, and — the actual defect —
 * it keys off the USER's locale rather than the TRACK's language, which is the wrong axis
 * entirely. Ops call `ctx.casing`, never `String.prototype`.
 *
 * What IS used: `String.prototype.toUpperCase` / `toLowerCase`, which implement the Unicode
 * DEFAULT case conversion. That is locale-independent, deterministic, and specified — so the
 * only thing left to implement here is the language-specific deltas, each of them small,
 * named, and testable.
 */

import type { CaseMapper } from '../../../contracts/cue-model.ts';

/** Turkish and Azeri share the dotted/dotless i rules. */
const DOTTED_LOCALES = new Set(['tr', 'az']);
const LITHUANIAN = 'lt';

const COMBINING_DOT_ABOVE = '\u0307';
const LATIN_CAPITAL_I_WITH_DOT = '\u0130'; // Turkish capital I with dot above
const LATIN_SMALL_DOTLESS_I = '\u0131'; // Turkish dotless small i

/** BCP-47 → the primary language subtag, lowercased. `null` and junk both yield null. */
export function primaryLanguage(locale: string | null | undefined): string | null {
  if (!locale) return null;
  const tag = locale.trim().toLowerCase();
  if (tag === '' || tag === 'und') return null;
  const [primary] = tag.split(/[-_]/);
  return primary && /^[a-z]{2,3}$/.test(primary) ? primary : null;
}

/**
 * Case + diacritic folding for 'stripped' comparisons (mergeSameText, dedupe).
 *
 * The algorithm, stated so the Python port folds identically:
 *   1. NFD-decompose, so a precomposed 'é' becomes 'e' + U+0301
 *   2. drop every combining mark (\p{Mn}\p{Me})
 *   3. lowercase (Unicode default, locale-independent — fold takes no locale by design)
 *   4. expand the characters NFD does NOT decompose, from the table below
 *   5. NFC-recompose, so the result is a normal string again
 *
 * Step 4 is a fixed list rather than a rule, because there is no rule: ß, ø, đ, ł, æ, œ, þ
 * and ð carry their difference in the base letter, not in a mark.
 */
const FOLD_EXPANSIONS: readonly (readonly [RegExp, string])[] = [
  [/ß/g, 'ss'],
  [/æ/g, 'ae'],
  [/œ/g, 'oe'],
  [/ø/g, 'o'],
  [/đ/g, 'd'],
  [/ð/g, 'd'],
  [/ł/g, 'l'],
  [/þ/g, 'th'],
  [/ı/g, 'i'],
  [/ŀ/g, 'l'],
  [/ŉ/g, 'n'],
];

const COMBINING_MARKS_RE = /[\p{Mn}\p{Me}]/gu;

export function foldText(s: string): string {
  let out = s.normalize('NFD').replace(COMBINING_MARKS_RE, '').toLowerCase();
  for (const [re, to] of FOLD_EXPANSIONS) out = out.replace(re, to);
  return out.normalize('NFC');
}

function upperFor(s: string, lang: string | null): string {
  if (lang !== null && DOTTED_LOCALES.has(lang)) {
    // Turkish/Azeri: i → İ, and the dotless ı → I (which the default already does).
    // Order matters: map i first so the İ produced here is not re-processed.
    return s.replace(/i/g, LATIN_CAPITAL_I_WITH_DOT).toUpperCase();
  }
  if (lang === LITHUANIAN) {
    // Lithuanian keeps the dot above on a lowercase i/j/į under accents; uppercasing
    // REMOVES it. Everything else is the default mapping.
    return s.replace(new RegExp(`([ijįIJĮ])${COMBINING_DOT_ABOVE}`, 'g'), '$1').toUpperCase();
  }
  return s.toUpperCase();
}

function lowerFor(s: string, lang: string | null): string {
  if (lang !== null && DOTTED_LOCALES.has(lang)) {
    // I → ı, İ → i. The default mapping produces 'i' for I and 'i'+U+0307 for İ, so both
    // need overriding, and the combining dot the default leaves behind has to go.
    return s
      .replace(/I/g, LATIN_SMALL_DOTLESS_I)
      .replace(new RegExp(LATIN_CAPITAL_I_WITH_DOT, 'g'), 'i')
      .toLowerCase();
  }
  // Default. NOTE: Unicode Final_Sigma is part of the DEFAULT lowercase mapping and is
  // implemented by every modern engine — 'ΟΔΟΣ' lowercases to 'οδος' with a final ς. It is
  // language-independent, so it needs no branch here; it is asserted in the tests so an
  // engine regression is caught rather than assumed away.
  return s.toLowerCase();
}

/** The injected implementation. Stateless, so one instance per worker is plenty. */
export function createCaseMapper(): CaseMapper {
  return {
    upper(s: string, locale: string | null): string {
      return upperFor(s, primaryLanguage(locale));
    },
    lower(s: string, locale: string | null): string {
      return lowerFor(s, primaryLanguage(locale));
    },
    fold(s: string): string {
      return foldText(s);
    },
  };
}
